import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchQuickItineraryDetails, saveQuickItineraryDetails } from "../repo/customizationRepo";

/** 按登录人、项目和需求版本隔离计调草稿，保存失败时保留输入。 */
export function useQuickItineraryDetails(
  projectId: string,
  demandId: string | undefined,
  options: { enabled: boolean; viewerId?: string; switching?: boolean },
) {
  const client = useQueryClient();
  const queryKey = ["customization", "quick-itinerary", options.viewerId ?? "", projectId, demandId];
  const draftKey = JSON.stringify(queryKey);
  const currentContext = useRef({ draftKey, enabled: options.enabled, switching: options.switching });
  currentContext.current = { draftKey, enabled: options.enabled, switching: options.switching };
  const visible = options.enabled && Boolean(demandId);
  const query = useQuery({
    queryKey,
    queryFn: () => fetchQuickItineraryDetails(projectId, demandId!),
    enabled: visible && !options.switching,
    retry: false,
  });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [saveError, setSaveError] = useState({ key: "", message: "" });
  const value = visible ? (drafts[draftKey] ?? query.data?.details ?? "") : "";
  const dirty = visible && drafts[draftKey] !== undefined && value !== (query.data?.details ?? "");
  const error = saveError.key === draftKey && saveError.message
    ? saveError.message
    : query.isError && visible ? "额外要求加载失败，请重试。" : "";

  return {
    visible, value, dirty, saving, error,
    loadFailed: visible && query.isError,
    loading: visible && (query.isPending || Boolean(options.switching)),
    retry: () => query.refetch(),
    setValue(next: string) {
      if (!visible) return;
      setDrafts((current) => ({ ...current, [draftKey]: next }));
      setSaveError({ key: draftKey, message: "" });
    },
    async save(): Promise<boolean> {
      if (!options.enabled) return true;
      if (!demandId || options.switching || savingRef.current || !query.isSuccess) return false;
      if (!dirty) return true;
      savingRef.current = true;
      setSaving(true);
      setSaveError({ key: draftKey, message: "" });
      try {
        const result = await saveQuickItineraryDetails(projectId, demandId, value);
        client.setQueryData(queryKey, result);
        setDrafts((current) => {
          if (current[draftKey] !== value) return current;
          const next = { ...current };
          delete next[draftKey];
          return next;
        });
        return currentContext.current.draftKey === draftKey &&
          currentContext.current.enabled && !currentContext.current.switching;
      } catch {
        setSaveError({ key: draftKey, message: "额外要求保存失败，请重试后再生成行程。" });
        return false;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
  };
}

export type QuickItineraryDetailsEditor = ReturnType<typeof useQuickItineraryDetails>;
